import { useMemo } from 'react';
import { logError } from '@core/logging';
import { useShareLink } from '@core/sharing';
import { decorateWithFWReferrer } from '@core/tracking';
import { ShareEnum } from '@core/types';
import { useLiveStreamActiveWhitelist } from './useLiveStreamActiveWhitelist';
const removeWWW = (url) => {
    return new URL(url).hostname.replace(/^www\./gi, '');
};
/**
 * Helper hook which provides information whether current livestream requires to be opened in
 * primary (canonical) url destination and a callback to open the livestream
 * by redirecting using share link in a first party url.
 *
 * @returns {object}
 *  `isFirstPartyLiveStream`: True means the livestream either already plays in first party
 *    context (current url matches primary url) or does not require to be played in primary
 *    url destination (no primary url is set),
 *
 *  `openInFirstParty`: Is a callback to open the livestream in first party url by
 *    using share link via `window.open`
 */
export const useFirstPartyLiveStream = (props) => {
    const { appContext, params, video } = props;
    const shareUrl = useShareLink({
        video,
        appContext,
        params,
        baseUrl: decorateWithFWReferrer(video?.canonical_url ?? '', {
            video,
            appContext,
            params,
        }),
        paramType: ShareEnum.QUERY,
    });
    const isWhitelisted = useLiveStreamActiveWhitelist();
    const isFirstPartyLiveStream = useMemo(() => {
        if (isWhitelisted ||
            !video?.canonical_url ||
            params?.livestream_firstparty) {
            return true;
        }
        try {
            if (video?.video_type === 'live_stream') {
                const currentUrl = params?.page_url ? removeWWW(params.page_url) : null;
                const firstPartyUrl = video?.canonical_url
                    ? removeWWW(video?.canonical_url)
                    : null;
                if (!currentUrl || firstPartyUrl !== currentUrl) {
                    return false;
                }
            }
        }
        catch (e) {
            logError(e);
        }
        return true;
    }, [isWhitelisted, params?.page_url, params?.livestream_firstparty, video]);
    const firstPartyUrl = useMemo(() => {
        if (isFirstPartyLiveStream) {
            return undefined;
        }
        if (!video || !params) {
            return undefined;
        }
        return shareUrl ?? undefined;
    }, [isFirstPartyLiveStream, video, params, shareUrl]);
    return useMemo(() => {
        const openInFirstParty = () => {
            try {
                if (firstPartyUrl) {
                    window.open(firstPartyUrl, 'first-party');
                }
            }
            catch (error) {
                logError(error);
            }
        };
        return { isFirstPartyLiveStream, openInFirstParty };
    }, [isFirstPartyLiveStream, firstPartyUrl]);
};
